import { useState, useMemo, useEffect } from "react";
import { type CampaignAutopilotConfigV2Data } from "@cede/types";
import { CYCLE_SAFE_MARGIN_MS } from "@cede/utils";
import { useAutomaticStreakBalance } from "../useAutomaticStreakBalance";
import { usePriorityTickets } from "../usePriorityTickets";
import { useDebounceValue } from "@/hooks/core/useDebounceFn";
import { safeDecimal } from "@/utils/hyperliquid/numeric";
import Decimal from "decimal.js";

// Debounce delay for target volume to limit minimum required balance API calls
const TARGET_VOLUME_DEBOUNCE_MS = 500;

// Worst-case per-cycle duration, shared with the Solo CMS and mirrored from the backend.
const SAFE_MARGIN_MS = new Decimal(CYCLE_SAFE_MARGIN_MS);
const MS_PER_MINUTE = new Decimal(60000);

export interface AutomaticStreakConfigFormV2Data {
  totalTargetVolume: number;
  /** If true, also create a streak for the next epoch */
  alsoRunForNextEpoch: boolean;
  /** Priority tickets to spend at queue join — each buys one prioritized epoch. 0 = plain join. */
  priorityTicketsToSpend: number;
}

interface UseAutomaticStreakConfigFormV2Props {
  accountId: string;
  exchangeId: string;
  pairSymbol: string;
  campaignName: string;
  epochId: number;
  config: CampaignAutopilotConfigV2Data;
  epochEndTime?: Date;
  hasNextEpoch?: boolean;
  onSubmit: (data: AutomaticStreakConfigFormV2Data) => void;
  onValidityChange?: (isValid: boolean) => void;
  /** Whether the epoch limit is reached (from useAutomaticStreakConfig hook) */
  isEpochLimitReached?: boolean;
}

export const useAutomaticStreakConfigFormV2 = ({
  accountId,
  exchangeId,
  pairSymbol,
  campaignName,
  epochId,
  config,
  epochEndTime,
  hasNextEpoch = false,
  onSubmit,
  onValidityChange,
  isEpochLimitReached = false,
}: UseAutomaticStreakConfigFormV2Props) => {
  // Form field states (declared before balance hook so targetVolume is available)
  const [targetVolumeInput, setTargetVolumeInput] = useState<string>(String(config.maxTargetVolume));
  const [alsoRunForNextEpoch, setAlsoRunForNextEpoch] = useState<boolean>(false);
  const [priorityTicketsToSpend, setPriorityTicketsToSpend] = useState<number>(0);

  const {
    totalTickets: priorityTicketBalance,
    isLoading: isPriorityTicketsLoading,
    isFeatureEnabled: isPriorityTicketsEnabled,
    isError: isPriorityTicketsError,
  } = usePriorityTickets();

  // Parsed values for calculations
  const targetVolume = safeDecimal(targetVolumeInput).toNumber();

  // Debounce target volume to limit minimum required balance API calls
  const { debouncedValue: debouncedTargetVolume, isDebouncing: isTargetVolumeDebouncing } = useDebounceValue(
    targetVolume,
    TARGET_VOLUME_DEBOUNCE_MS,
  );

  // Balance hook with V2 config for proper balance calculations
  const {
    balance,
    quoteToken,
    isLoading: isBalanceLoading,
    isMinimumBalanceLoading,
    estimateCyclesV2,
    requiredBalanceV2,
    hasEnoughBalanceV2,
  } = useAutomaticStreakBalance({
    accountId,
    exchangeId,
    pairSymbol,
    autopilotConfigV2: config,
    campaignName,
    epochId,
    totalTargetVolume: debouncedTargetVolume,
  });

  // Calculate estimated duration based on epoch end time (V2 always runs until epoch ends)
  const estimatedDurationSeconds = useMemo(() => {
    if (epochEndTime) {
      const now = new Date();
      const msUntilEnd = epochEndTime.getTime() - now.getTime();
      return Math.max(0, Math.floor(msUntilEnd / 1000));
    }
    return 0;
  }, [epochEndTime]);

  // Estimate number of cycles - delegated to balance hook
  const estimatedCycles = useMemo(() => {
    return estimateCyclesV2(targetVolume);
  }, [estimateCyclesV2, targetVolume]);

  // Required balance (minimum to create autopilot) - from API
  const requiredBalance = requiredBalanceV2;

  const isAnyBalanceLoading = isBalanceLoading || isMinimumBalanceLoading || isTargetVolumeDebouncing;

  // Balance sufficiency check - delegated to balance hook
  const isBalanceSufficient = useMemo(() => {
    if (isAnyBalanceLoading) return true; // Assume sufficient while loading
    return hasEnoughBalanceV2();
  }, [isAnyBalanceLoading, hasEnoughBalanceV2]);

  // Blocking validation errors (gate submission).
  const validationErrors = useMemo(() => {
    const newErrors: Record<string, string> = {};

    if (targetVolume < config.minTargetVolume) {
      newErrors.targetVolume = `Min ${config.minTargetVolume} ${quoteToken}`;
    } else if (targetVolume > config.maxTargetVolume) {
      newErrors.targetVolume = `Max ${config.maxTargetVolume} ${quoteToken}`;
    }

    return newErrors;
  }, [targetVolume, config, quoteToken]);

  // Non-blocking epoch-runway warning. When the config can't finish in the time
  // left this epoch, the user can still join the queue — the picker just skips
  // them this epoch (config-bounds runway snapshot on the queue item) and starts
  // them in the next one, which begins with full runway. Mirrors the backend's
  // per-cycle `CYCLE_SAFE_MARGIN_MS` so the warning matches the picker's gate.
  const epochTimeWarning = useMemo(() => {
    if (!epochEndTime || targetVolume <= 0) return null;

    const msUntilEnd = new Decimal(epochEndTime.getTime() - Date.now());
    const minRequiredTimeMs = SAFE_MARGIN_MS.mul(estimatedCycles);
    if (msUntilEnd.gte(minRequiredTimeMs)) return null;

    const minutesNeeded = minRequiredTimeMs.div(MS_PER_MINUTE).ceil().toNumber();
    // Clamp to 0 to avoid negative values when epoch has already ended
    const minutesRemaining = Math.max(0, msUntilEnd.div(MS_PER_MINUTE).floor().toNumber());
    return `This epoch ends in ${minutesRemaining} min${minutesRemaining !== 1 ? "s" : ""}, but your streak needs about ${minutesNeeded}. Join now and it'll start in the next epoch.`;
  }, [epochEndTime, targetVolume, estimatedCycles]);

  // Form validity. The epoch-runway warning is intentionally NOT a gate — joining
  // the queue is always allowed. `isEpochLimitReached` is likewise not a gate; the
  // picker materializes queued users into real streaks as soon as a slot opens up.
  const isFormValid = useMemo(() => {
    return (
      Object.keys(validationErrors).length === 0 && targetVolume > 0 && isBalanceSufficient && !isAnyBalanceLoading
    );
  }, [validationErrors, targetVolume, isBalanceSufficient, isAnyBalanceLoading]);

  // Notify parent of validity changes
  useEffect(() => {
    onValidityChange?.(isFormValid);
  }, [isFormValid, onValidityChange]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      return;
    }

    onSubmit({
      totalTargetVolume: targetVolume,
      alsoRunForNextEpoch,
      // Clamp to [0, live balance] so a stale UI can neither overspend nor send a negative count.
      priorityTicketsToSpend: Math.max(0, Math.min(priorityTicketsToSpend, priorityTicketBalance)),
    });
  };

  return {
    // Form field values
    fields: {
      targetVolume: targetVolumeInput,
      targetVolumeParsed: targetVolume,
      alsoRunForNextEpoch,
      priorityTicketsToSpend,
    },
    // Form field setters
    setters: {
      setTargetVolume: setTargetVolumeInput,
      setAlsoRunForNextEpoch,
      setPriorityTicketsToSpend,
    },
    // Priority ticket balance (spendable at join to buy prioritized epochs)
    priorityTickets: {
      balance: priorityTicketBalance,
      isLoading: isPriorityTicketsLoading,
      isFeatureEnabled: isPriorityTicketsEnabled,
      isError: isPriorityTicketsError,
    },
    // Balance info
    balance: {
      value: balance,
      quoteToken,
      isLoading: isBalanceLoading,
      isMinimumBalanceLoading: isMinimumBalanceLoading || isTargetVolumeDebouncing,
      requiredBalance,
      isSufficient: isBalanceSufficient,
    },
    // Calculated values
    calculations: {
      estimatedDurationSeconds,
      estimatedCycles,
    },
    // Validation
    validation: {
      errors: validationErrors,
      epochTimeWarning,
      isFormValid,
      isEpochLimitReached,
    },
    // Config bounds (for display)
    configBounds: {
      minTargetVolume: config.minTargetVolume,
      maxTargetVolume: config.maxTargetVolume,
      minTradeSize: config.minTradeSize,
      maxTradeSize: config.maxTradeSize,
    },
    // Whether next epoch option should be shown
    hasNextEpoch,
    // Handlers
    handleSubmit,
  };
};
